export type PetCareTemplateKey = 'vet-visit' | 'vaccination' | 'grooming' | 'cleaning' | 'flea-worm';

export type PetCareLogType = 'vet-visit' | 'vaccination' | 'grooming' | 'cleaning' | 'other';

export type PetCareReminderType = 'gp' | 'vaccination' | 'other';

type PetCareTemplateDefinition = {
  key: PetCareTemplateKey;
  label: string;
  description: string;
  reminderType: PetCareReminderType;
  frequencyMonths: number | null;
  reminderDaysBefore: number | null;
  notes: string;
};

type PetCareLogDefinition = {
  key: PetCareLogType;
  label: string;
  reminderType: PetCareReminderType;
  notes: string;
};

export type PetCareTemplate = PetCareTemplateDefinition & {
  title: string;
};

export type PetCareLogOption = PetCareLogDefinition & {
  title: string;
};

const PET_CARE_TEMPLATE_DEFINITIONS: readonly PetCareTemplateDefinition[] = [
  {
    key: 'vet-visit',
    label: 'Vet visit',
    description: 'Keep check-ups, follow-ups, and next due dates visible in Health.',
    reminderType: 'gp',
    frequencyMonths: 12,
    reminderDaysBefore: 7,
    notes: 'Use this for check-ups, follow-ups, or any date the vet wants to see again.',
  },
  {
    key: 'vaccination',
    label: 'Vaccination',
    description: 'Useful for boosters, annual vaccines, or dated follow-up shots.',
    reminderType: 'vaccination',
    frequencyMonths: 12,
    reminderDaysBefore: 14,
    notes: 'Keep boosters and vaccination dates visible here.',
  },
  {
    key: 'grooming',
    label: 'Grooming',
    description: 'Helpful for grooming, clipping, nail trims, or coat care that repeats.',
    reminderType: 'other',
    frequencyMonths: 2,
    reminderDaysBefore: 3,
    notes: 'Helpful for grooming, clipping, nail trims, or coat care that repeats.',
  },
  {
    key: 'cleaning',
    label: 'Cleaning',
    description: 'Use this for litter trays, hutches, tanks, cages, or pet-area cleaning jobs.',
    reminderType: 'other',
    frequencyMonths: null,
    reminderDaysBefore: 0,
    notes: 'Use this for litter trays, hutches, tanks, cages, or pet-area cleaning jobs.',
  },
  {
    key: 'flea-worm',
    label: 'Flea / worm',
    description: 'Keep preventative treatments visible before they drift.',
    reminderType: 'other',
    frequencyMonths: 1,
    reminderDaysBefore: 3,
    notes: 'Keep flea, tick, or worm treatment timing visible before it drifts.',
  },
] as const;

const PET_CARE_LOG_DEFINITIONS: readonly PetCareLogDefinition[] = [
  {
    key: 'vet-visit',
    label: 'Vet visit',
    reminderType: 'gp',
    notes: 'Log the visit and keep the main outcome handy.',
  },
  {
    key: 'vaccination',
    label: 'Vaccination',
    reminderType: 'vaccination',
    notes: 'Useful for booster dates and vaccine notes.',
  },
  {
    key: 'grooming',
    label: 'Grooming',
    reminderType: 'other',
    notes: 'Use this for trims, clipping, grooming, or coat care.',
  },
  {
    key: 'cleaning',
    label: 'Cleaning',
    reminderType: 'other',
    notes: 'Use this for litter, tank, cage, bedding, or other pet-space cleaning.',
  },
  {
    key: 'other',
    label: 'Other care',
    reminderType: 'other',
    notes: 'Use this for any other pet-care job you want recorded here.',
  },
] as const;

function safePetName(value: string | null | undefined) {
  const trimmed = String(value || '').trim();
  return trimmed.length ? trimmed : 'this pet';
}

function buildTemplateTitle(key: PetCareTemplateKey, petName: string) {
  const safeName = safePetName(petName);
  switch (key) {
    case 'vet-visit':
      return `Vet visit for ${safeName}`;
    case 'vaccination':
      return `${safeName} vaccination / booster`;
    case 'grooming':
      return `${safeName} grooming`;
    case 'cleaning':
      return `${safeName} cleaning routine`;
    case 'flea-worm':
      return `${safeName} flea / worm treatment`;
  }
}

function buildLogTitle(key: PetCareLogType, petName: string) {
  const safeName = safePetName(petName);
  switch (key) {
    case 'vet-visit':
      return `Vet visit for ${safeName}`;
    case 'vaccination':
      return `${safeName} vaccination`;
    case 'grooming':
      return `${safeName} grooming`;
    case 'cleaning':
      return `${safeName} cleaning`;
    case 'other':
      return `Care update for ${safeName}`;
  }
}

export function buildPetCareTemplates(petName: string) {
  return PET_CARE_TEMPLATE_DEFINITIONS.map((definition) => ({
    ...definition,
    title: buildTemplateTitle(definition.key, petName),
  }));
}

export function buildPetCareLogOptions(petName: string) {
  return PET_CARE_LOG_DEFINITIONS.map((definition) => ({
    ...definition,
    title: buildLogTitle(definition.key, petName),
  }));
}

export function mapPetCareLogTypeToReminderType(value: PetCareLogType) {
  return PET_CARE_LOG_DEFINITIONS.find((definition) => definition.key === value)?.reminderType || 'other';
}

export function buildCompletedReminderTimestamp(dateIso: string) {
  const trimmed = String(dateIso || '').trim();
  if (!trimmed) return null;
  return `${trimmed}T12:00:00.000Z`;
}
